// PWA Detection and Device Utilities
// QRCodeXpress - Mobile/Tablet Detection

export const DeviceDetection = {
  
  // Check if running as PWA
  isPWA() {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true ||
           document.referrer.includes('android-app://');
  },

  // Check if mobile device
  isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  // Check if tablet
  isTablet() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent);
    return isTablet;
  },

  // Check if iOS
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  },

  // Check if Android
  isAndroid() {
    return /Android/i.test(navigator.userAgent);
  },

  // Get device type
  getDeviceType() {
    if (this.isTablet()) return 'tablet';
    if (this.isMobile()) return 'mobile';
    return 'desktop';
  },

  // Get screen orientation
  getOrientation() {
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    }
    return 'landscape';
  },

  // Check if touch device
  isTouchDevice() {
    return ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0) ||
           (navigator.msMaxTouchPoints > 0);
  },

  // Get viewport dimensions
  getViewport() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      orientation: this.getOrientation()
    };
  },

  // Check if has network connection
  isOnline() {
    return navigator.onLine;
  },

  // Get connection type (if available)
  getConnectionType() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return null;
  },

  // Check if low-power mode
  isLowPowerMode() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return connection?.saveData === true;
  },

  // Add device class to body
  addDeviceClasses() {
    const body = document.body;
    
    // Device type
    body.classList.add(`device-${this.getDeviceType()}`);
    
    // Orientation
    body.classList.add(`orientation-${this.getOrientation()}`);
    
    // PWA mode
    if (this.isPWA()) {
      body.classList.add('pwa-mode');
    }
    
    // Platform
    if (this.isIOS()) {
      body.classList.add('platform-ios');
    } else if (this.isAndroid()) {
      body.classList.add('platform-android');
    }
    
    // Touch support
    if (this.isTouchDevice()) {
      body.classList.add('touch-device');
    }
    
    // Online status
    if (!this.isOnline()) {
      body.classList.add('offline');
    }
  },

  // Update orientation class on resize
  watchOrientation(callback) {
    window.addEventListener('resize', () => {
      const body = document.body;
      body.classList.remove('orientation-portrait', 'orientation-landscape');
      body.classList.add(`orientation-${this.getOrientation()}`);
      
      if (callback) {
        callback(this.getOrientation());
      }
    });
  },

  // Watch online/offline status
  watchOnlineStatus(onlineCallback, offlineCallback) {
    window.addEventListener('online', () => {
      document.body.classList.remove('offline');
      if (onlineCallback) onlineCallback();
    });

    window.addEventListener('offline', () => {
      document.body.classList.add('offline');
      if (offlineCallback) offlineCallback();
    });
  }
};

export const PWAInstall = {
  
  // Store install prompt
  deferredPrompt: null,

  // Check if can be installed
  canInstall() {
    return this.deferredPrompt !== null;
  },

  // Initialize install prompt listener
  init(onInstallAvailable) {
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      this.deferredPrompt = e;
      
      if (onInstallAvailable) {
        onInstallAvailable();
      }
    });

    // Track install success
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      this.deferredPrompt = null;
    });
  },

  // Show install prompt
  async promptInstall() {
    if (!this.deferredPrompt) {
      console.log('[PWA] Install prompt not available');
      return false;
    }

    this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    
    console.log(`[PWA] User choice: ${outcome}`);
    this.deferredPrompt = null;
    
    return outcome === 'accepted';
  }
};

export const PWAStorage = {
  
  // IndexedDB wrapper for large data
  async openDB(dbName = 'QRCodeXpress', version = 1) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores
        if (!db.objectStoreNames.contains('qrcodes')) {
          const qrStore = db.createObjectStore('qrcodes', { keyPath: 'id', autoIncrement: true });
          qrStore.createIndex('createdAt', 'createdAt', { unique: false });
          qrStore.createIndex('type', 'type', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('offline-queue')) {
          db.createObjectStore('offline-queue', { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  },

  // Save QR code offline
  async saveQROffline(qrData) {
    const db = await this.openDB();
    const transaction = db.transaction(['qrcodes'], 'readwrite');
    const store = transaction.objectStore('qrcodes');
    
    const data = {
      ...qrData,
      createdAt: new Date().toISOString(),
      synced: false
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  // Get all offline QR codes
  async getOfflineQRs() {
    const db = await this.openDB();
    const transaction = db.transaction(['qrcodes'], 'readonly');
    const store = transaction.objectStore('qrcodes');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  // Clear synced QR codes
  async clearSyncedQRs() {
    const db = await this.openDB();
    const transaction = db.transaction(['qrcodes'], 'readwrite');
    const store = transaction.objectStore('qrcodes');
    const index = store.index('synced');
    
    const request = index.openCursor(IDBKeyRange.only(true));
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  }
};

export const PWAPerformance = {
  
  // Check if should use reduced data
  shouldReduceData() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return connection?.saveData === true || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g';
  },

  // Lazy load images
  lazyLoadImages() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img.lazy').forEach(img => {
        imageObserver.observe(img);
      });
    }
  },

  // Prefetch critical resources
  prefetchResources(urls) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        urls.forEach(url => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = url;
          document.head.appendChild(link);
        });
      });
    }
  },

  // Measure performance
  logPerformance() {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        const connectTime = perfData.responseEnd - perfData.requestStart;
        const renderTime = perfData.domComplete - perfData.domLoading;
        
        console.log('[PWA Performance]', {
          pageLoadTime: `${pageLoadTime}ms`,
          connectTime: `${connectTime}ms`,
          renderTime: `${renderTime}ms`
        });
      });
    }
  }
};

// Initialize on load
export function initializePWA() {
  // Add device classes
  DeviceDetection.addDeviceClasses();
  
  // Watch orientation changes
  DeviceDetection.watchOrientation((orientation) => {
    console.log('[PWA] Orientation changed:', orientation);
  });
  
  // Watch online/offline
  DeviceDetection.watchOnlineStatus(
    () => console.log('[PWA] Back online'),
    () => console.log('[PWA] Gone offline')
  );
  
  // Log device info
  console.log('[PWA] Device:', {
    type: DeviceDetection.getDeviceType(),
    isPWA: DeviceDetection.isPWA(),
    viewport: DeviceDetection.getViewport(),
    connection: DeviceDetection.getConnectionType()
  });
}
